import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';

export interface Language {
  code: string;
  name: string;
  flag: string;
}

export interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (language: string) => void;
  availableLanguages: Language[];
  isTranslationAvailable: (tutorialId: string, targetLanguage?: string) => Promise<boolean>;
  requestTranslation: (tutorialId: string, targetLanguage: string) => Promise<string>;
  getTranslationProgress: (translationId: string) => Promise<any>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' }
];

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>('en');

  // Load language preference from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language');
    if (savedLanguage && SUPPORTED_LANGUAGES.some(lang => lang.code === savedLanguage)) {
      setCurrentLanguage(savedLanguage);
    } else {
      // Try to detect browser language
      const browserLanguage = navigator.language.split('-')[0];
      if (SUPPORTED_LANGUAGES.some(lang => lang.code === browserLanguage)) {
        setCurrentLanguage(browserLanguage);
      }
    }
  }, []);

  // Save language preference to localStorage
  const setLanguage = (language: string) => {
    setCurrentLanguage(language);
    localStorage.setItem('preferred-language', language);
    
    // Trigger platform translation if needed
    if (language !== 'en') {
      // This will be handled by the usePlatformTranslation hook
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language } }));
    }
  };

  // Check if translation is available for a tutorial (defaults to Spanish 'es')
  const isTranslationAvailable = async (tutorialId: string, targetLanguage: string = 'es'): Promise<boolean> => {
    try {
      const response = await apiService.getTranslationLanguages(tutorialId);
      return response.success && Array.isArray(response.data) && response.data.includes(targetLanguage);
    } catch (error) {
      console.error('Error checking translation availability:', error);
      return false;
    }
  };

  // Request translation for a tutorial
  const requestTranslation = async (tutorialId: string, targetLanguage: string): Promise<string> => {
    try {
      const response = await apiService.request('/translation/start', {
        method: 'POST',
        body: JSON.stringify({
          tutorialId,
          targetLanguage
        })
      });

      // Show notification about draft creation
      if (response.data.draftId) {
        console.log(`Translation started! Draft created with ID: ${response.data.draftId}`);
        // You could show a toast notification here
      }

      return response.data.id; // Return translation ID
    } catch (error) {
      console.error('Error requesting translation:', error);
      throw error;
    }
  };

  // Get translation progress
  const getTranslationProgress = async (translationId: string): Promise<any> => {
    try {
      const response = await apiService.request(`/translation/progress/${translationId}`);
      return response.success ? response.data : null;
    } catch (error) {
      console.error('Error getting translation progress:', error);
      return null;
    }
  };

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    availableLanguages: SUPPORTED_LANGUAGES,
    isTranslationAvailable,
    requestTranslation,
    getTranslationProgress
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
